import { createContext, useCallback, useMemo, useState } from 'react';

/**
 * @import {
 *   Plugin,
 *   PluginContextValue
 * } from '../../types';
 */

/** @type {PluginContextValue} */
const defaultContextValue = {
  plugins: [],
  registerPlugin: (plugin) => {},
  unregisterPlugin: (plugin) => {},
  getPlugins: (pluginPoint) => []
};

export const PluginContext = createContext(defaultContextValue);

const EMPTY_PLUGINS = [];

/**
 * Hook to create the plugin provider value.
 *
 * @param {Plugin[]} [defaultValue] - Initial plugins
 *
 * @returns {PluginContextValue}
 */
export const usePluginsProviderValue = (defaultValue = EMPTY_PLUGINS) => {
  const [ plugins, setPlugins ] = useState(defaultValue);

  const registerPlugin = useCallback((plugin) => {
    setPlugins((prevPlugins) => [ ...prevPlugins, plugin ]);
  }, []);

  const unregisterPlugin = useCallback((plugin) => {
    setPlugins((prevPlugins) =>
      prevPlugins.filter((p) => p !== plugin)
    );
  }, []);

  const getPlugins = useCallback((type) => {
    return plugins
      .filter((plugin) => plugin.type === type)
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [ plugins ]);

  return useMemo(() => ({
    plugins,
    registerPlugin,
    unregisterPlugin,
    getPlugins
  }), [ plugins, registerPlugin, unregisterPlugin, getPlugins ]);
};
